import { useEffect, useMemo, useState } from 'react';

import { type WriteContractReturnType } from '@wagmi/core';
import { useSearchParams } from 'react-router-dom';
import Banner from 'shared/lib/components/banner/Banner';
import AppPage from 'shared/lib/components/common/AppPage';
import { Display, Text } from 'shared/lib/components/common/Typography';
import {
  STACKING_SALMON_BORROWER_NFT_SIMPLE_MANAGER_ADDRESS,
  STACKING_SALMON_PERMIT2_MANAGER_ADDRESS,
  STACKING_SALMON_UNISWAP_NFT_MANAGER_ADDRESS,
  getChainLogo,
} from 'shared/lib/data/constants/ChainSpecific';
import { GREY_400, GREY_600 } from 'shared/lib/data/constants/Colors';
import { Token } from 'shared/lib/data/Token';
import { useBorrowerNfts } from 'shared/lib/hooks/UseBorrowerNft';
import useChain from 'shared/lib/hooks/UseChain';
import { useLendingPairsBalances } from 'shared/lib/hooks/UseLendingPairBalances';
import { useLendingPairs } from 'shared/lib/hooks/UseLendingPairs';
import { useLatestPriceRelay } from 'shared/lib/hooks/UsePriceRelay';
import { useTokenColors } from 'shared/lib/hooks/UseTokenColors';
import { useUniswapPools } from 'shared/lib/hooks/UseUniswapPools';
import { formatUSDAuto } from 'shared/lib/util/Numbers';
import styled from 'styled-components';
import { zeroAddress } from 'viem';
import { base, berachainTestnetbArtio } from 'viem/chains';
import { useAccount, useBlockNumber, usePublicClient } from 'wagmi';
// import { useCapabilities } from 'wagmi/experimental';

import PendingTxnModal, { PendingTxnModalStatus } from '../components/common/PendingTxnModal';
import BorrowingWidget from '../components/markets/borrow/BorrowingWidget';
import LiquidateTab from '../components/markets/liquidate/LiquidateTab';
import InfoTab from '../components/markets/monitor/InfoTab';
import SupplyTable, { SupplyTableRow } from '../components/markets/supply/SupplyTable';
import { useDeprecatedMarginAccountShim } from '../hooks/useDeprecatedMarginAccountShim';

const SECONDARY_COLOR = '#FFFFFF';
const SELECTED_TAB_KEY = 'selectedTab';

const HeaderDividingLine = styled.hr`
  color: ${GREY_600};
  height: 1px;
`;

const HeaderSegmentedControlOption = styled.button.attrs((props: { isActive: boolean }) => props)`
  padding: 16px;
  cursor: pointer;
  color: ${(props) => (props.isActive ? '#FFFFFF' : '#EC5D2A')};

  &:hover {
    color: #FFFFFF;
  }

  position: relative;
  &:after {
    position: absolute;
    content: ${(props) => (props.isActive ? "''" : 'none')};
    background-color: #FFFFFF;

    top: 100%;
    left: 0px;
    width: 100%;
    height: 1px;
  }
`;

export type TokenBalance = {
  token: Token;
  balance: number;
  balanceUSD: number;
  isKitty: boolean;
  apy: number;
  pairName: string;
};

enum TabOption {
  Supply = 'supply',
  Borrow = 'borrow',
  Monitor = 'monitor',
  Liquidate = 'liquidate',
  Rewards = 'rewards',
}

export default function MarketsPage() {

  const activeChain = useChain();
  // MARK: component state
  const [pendingTxn, setPendingTxn] = useState<WriteContractReturnType | null>(null);
  const [isPendingTxnModalOpen, setIsPendingTxnModalOpen] = useState(false);
  const [pendingTxnModalStatus, setPendingTxnModalStatus] = useState<PendingTxnModalStatus | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const selectedTab = useMemo(() => {
    const tabSearchParam = searchParams.get(SELECTED_TAB_KEY)?.toLowerCase();
    if (tabSearchParam != null) {
      // Check if the search param is a valid tab (case insensitive)
      if (Object.values(TabOption).includes(tabSearchParam.toLowerCase() as TabOption)) {
        return tabSearchParam as TabOption;
      }
    }
    return TabOption.Supply;
  }, [searchParams]);

  // MARK: wagmi hooks
  const { address: userAddress } = useAccount();
  // const { data: capabilities } = useCapabilities({ account: userAddress });
  const hasAuxiliaryFunds = useMemo(
    // NOTE: We only expect `auxiliaryFunds.supported` to be true for the Coinbase Smart Wallet,
    // which only _actually_ supports auxiliary funds on Base -- hence the extra condition.
    () => activeChain.id === base.id,
    [activeChain.id]
  );

  // MARK: custom hooks
  const { lendingPairs, refetchOracleData, refetchLenderData } = useLendingPairs(activeChain.id);
  const { data: tokenColors } = useTokenColors(lendingPairs);
  const { data: tokenQuotes } = useLatestPriceRelay(lendingPairs);
  const { balances: balancesMap, refetch: refetchBalances } = useLendingPairsBalances(lendingPairs, activeChain.id);
  const uniswapPools = useUniswapPools(lendingPairs);

  const borrowerNftFilterParams = useMemo(
    () => ({
      includeUnusedBorrowers: false,
      onlyCheckMostRecentModify: true,
      validManagerSet: new Set([
        STACKING_SALMON_PERMIT2_MANAGER_ADDRESS[activeChain.id],
        STACKING_SALMON_BORROWER_NFT_SIMPLE_MANAGER_ADDRESS[activeChain.id],
        STACKING_SALMON_UNISWAP_NFT_MANAGER_ADDRESS[activeChain.id],
      ]),
    }),
    [activeChain.id]
  );
  const { borrowerNfts, refetchBorrowerNftRefs, refetchBorrowers } = useBorrowerNfts(
    uniswapPools,
    userAddress,
    activeChain.id,
    borrowerNftFilterParams
  );
  const borrowers = useDeprecatedMarginAccountShim(lendingPairs, borrowerNfts);

  // Poll for `blockNumber` when app is in the foreground. Not much different than a `useInterval` that stops
  // when in the background
  const { data: blockNumber } = useBlockNumber({
    chainId: activeChain.id,
    cacheTime: 5_000,
    watch: false,
    query: {
      refetchOnMount: false,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchInterval: 5_000,
      refetchIntervalInBackground: false,
    },
  });
  // Use the `blockNumber` to trigger refresh of certain data
  useEffect(() => {
    if (activeChain.id === berachainTestnetbArtio.id || !blockNumber) return;
    // NOTE: Due to polling, we don't receive every block, so this bisection isn't perfect. Close enough though.
    if (blockNumber % 2n) {
      refetchOracleData();
    } else if (selectedTab === TabOption.Borrow) {
      refetchBorrowers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockNumber]);

  const doesGuardianSenseManipulation = useMemo(() => {
    return lendingPairs.some((pair) => pair.oracleData.manipulationMetric > pair.manipulationThreshold);
  }, [lendingPairs]);

  const publicClient = usePublicClient({ chainId: activeChain.id });
  useEffect(() => {
    (async () => {
      if (!pendingTxn || !publicClient) return;
      setPendingTxnModalStatus(PendingTxnModalStatus.PENDING);
      setIsPendingTxnModalOpen(true);
      const receipt = await publicClient.waitForTransactionReceipt({
        hash: pendingTxn,
      });
      if (receipt.status === 'success') {
        setPendingTxnModalStatus(PendingTxnModalStatus.SUCCESS);
      } else {
        setPendingTxnModalStatus(PendingTxnModalStatus.FAILURE);
      }
    })();
  }, [publicClient, pendingTxn, setIsPendingTxnModalOpen, setPendingTxnModalStatus]);

  const supplyRows = useMemo(() => {
    const rows: SupplyTableRow[] = [];
    const ethBalance = balancesMap.get(zeroAddress);
    lendingPairs.forEach((pair) => {
      const isToken0Weth = pair.token0.name === 'Wrapped BERA';
      const isToken1Weth = pair.token1.name === 'Wrapped BERA';

      const token0Price = tokenQuotes?.get(pair.token0.symbol) || 0;
      const token1Price = tokenQuotes?.get(pair.token1.symbol) || 0;
      const token0Balance =
        (balancesMap.get(pair.token0.address)?.value || 0) + ((isToken0Weth && ethBalance?.value) || 0);
      const token1Balance =
        (balancesMap.get(pair.token1.address)?.value || 0) + ((isToken1Weth && ethBalance?.value) || 0);
      const kitty0Balance = balancesMap.get(pair.kitty0.address)?.value || 0;
      const kitty1Balance = balancesMap.get(pair.kitty1.address)?.value || 0;

      rows.push({
        asset: pair.token0,
        kitty: pair.kitty0,
        apy: pair.kitty0Info.lendAPY * 100,
        rewardsRate: pair.rewardsRate0,
        collateralAssets: [pair.token0, pair.token1],
        totalSupply: pair.kitty0Info.totalAssets.toNumber(),
        suppliedBalance: kitty0Balance,
        suppliableBalance: token0Balance,
        withdrawableBalance: pair.kitty0Info.availableAssets.toNumber(),
        isOptimized: true,
        lastUpdated: pair.lastWrite.getTime(),
        ...(token0Price > 0
          ? {
              totalSupplyUsd: pair.kitty0Info.totalAssets.toNumber() * token0Price,
              suppliedBalanceUsd: kitty0Balance * token0Price,
              suppliableBalanceUsd: token0Balance * token0Price,
            }
          : {}),
      });
      rows.push({
        asset: pair.token1,
        kitty: pair.kitty1,
        apy: pair.kitty1Info.lendAPY * 100,
        rewardsRate: pair.rewardsRate1,
        collateralAssets: [pair.token1, pair.token0],
        totalSupply: pair.kitty1Info.totalAssets.toNumber(),
        suppliedBalance: kitty1Balance,
        suppliableBalance: token1Balance,
        withdrawableBalance: pair.kitty1Info.availableAssets.toNumber(),
        isOptimized: true,
        lastUpdated: pair.lastWrite.getTime(),
        ...(token1Price > 0
          ? {
              totalSupplyUsd: pair.kitty1Info.totalAssets.toNumber() * token1Price,
              suppliedBalanceUsd: kitty1Balance * token1Price,
              suppliableBalanceUsd: token1Balance * token1Price,
            }
          : {}),
      });
    });
    return rows;
  }, [balancesMap, lendingPairs, tokenQuotes]);

  let tabContent: JSX.Element;

  switch (selectedTab) {
    default:
    case TabOption.Supply:
      tabContent = (
        <SupplyTable hasAuxiliaryFunds={hasAuxiliaryFunds} rows={supplyRows} setPendingTxn={setPendingTxn} />
      );
      break;
    case TabOption.Borrow:
      tabContent = (
        <BorrowingWidget
          chain={activeChain}
          userAddress={userAddress}
          borrowers={borrowers}
          lendingPairs={lendingPairs}
          tokenBalances={balancesMap}
          tokenQuotes={tokenQuotes!}
          tokenColors={tokenColors!}
          setPendingTxn={setPendingTxn}
        />
      );
      break;
    case TabOption.Monitor:
      tabContent = (
        <InfoTab
          chainId={activeChain.id}
          lendingPairs={lendingPairs}
          tokenColors={tokenColors!}
          setPendingTxn={setPendingTxn}
        />
      );
      break;
    case TabOption.Liquidate:
      tabContent = (
        <LiquidateTab
          chainId={activeChain.id}
          lendingPairs={lendingPairs}
          tokenQuotes={tokenQuotes!}
          setPendingTxn={setPendingTxn}
        />
      );
      break;
      // case TabOption.Rewards:
      //   tabContent = (
      //     <RewardsTab
      //       chainId={activeChain.id}
      //       lendingPairs={lendingPairs}
      //       tokenColors={tokenColors!}
      //       setPendingTxn={setPendingTxn}
      //     />
      //   );
      //   break;
  }

  const totalSupplied = useMemo(() => {
    const totalAssets = supplyRows.reduce((acc, row) => acc + (row.totalSupplyUsd || 0), 0);
    return totalAssets;
  }, [supplyRows]);

  const totalBorrowed = useMemo(() => {
    return lendingPairs.reduce((acc, pair) => {
      const token0Price = tokenQuotes?.get(pair.token0.symbol) || 0;
      const token1Price = tokenQuotes?.get(pair.token1.symbol) || 0;
      const token0BorrowedUsd = pair.kitty0Info.totalBorrows.toNumber() * token0Price;
      const token1BorrowedUsd = pair.kitty1Info.totalBorrows.toNumber() * token1Price;
      return acc + token0BorrowedUsd + token1BorrowedUsd;
    }, 0);
  }, [lendingPairs, tokenQuotes]);

  const totalAvailable = useMemo(() => {
    return totalSupplied - totalBorrowed;
  }, [totalSupplied, totalBorrowed]);

  const activeChainLogo = getChainLogo(activeChain.id, 32);

  return (
    <>
    <Banner
    bannerName='Henlo'
    bannerText={`Stacking Salmon is currently in beta, please use at your own risk. 
      Audits are currently in progress.`}
    bannerColor='#EC5D2A'
    />
    <AppPage>
      <div className='flex flex-col gap-2 max-w-screen-xl m-auto'>
        <div className='flex flex-row items-center gap-4 mb-4'>
          {activeChainLogo}
          <Text size='XXL' >{activeChain.name} Salmon Markets</Text>
        </div>
        <div className='flex flex-row gap-8 ml-1 mb-2'>
          <div className='flex flex-col'>
            <Text size='M' weight='bold' color='#EC5D2A'>
              Total Supplied
            </Text>
            <Display size='S' weight='semibold'>
              {formatUSDAuto(totalSupplied)}
            </Display>
          </div>
          <div className='flex flex-col'>
            <Text size='M' weight='bold' color='#EC5D2A'>
              Total Available
            </Text>
            <Display size='S' weight='semibold'>
              {formatUSDAuto(totalAvailable)}
            </Display>
          </div>
          <div className='flex flex-col'>
            <Text size='M' weight='bold' color='#EC5D2A'>
              Total Borrowed
            </Text>
            <Display size='S' weight='semibold'>
              {formatUSDAuto(totalBorrowed)}
            </Display>
          </div>
        </div>
        <div>
          <div className='flex flex-row' role='tablist'>
            <HeaderSegmentedControlOption
              isActive={selectedTab === TabOption.Supply}
              onClick={() => setSearchParams({ [SELECTED_TAB_KEY]: TabOption.Supply })}
              role='tab'
              aria-selected={selectedTab === TabOption.Supply}
            >
              Supply
            </HeaderSegmentedControlOption>
            <HeaderSegmentedControlOption
              isActive={selectedTab === TabOption.Borrow}
              onClick={() => setSearchParams({ [SELECTED_TAB_KEY]: TabOption.Borrow })}
              role='tab'
              aria-selected={selectedTab === TabOption.Borrow}
            >
              Borrow
            </HeaderSegmentedControlOption>
            <HeaderSegmentedControlOption
              isActive={selectedTab === TabOption.Monitor}
              onClick={() => setSearchParams({ [SELECTED_TAB_KEY]: TabOption.Monitor })}
              role='tab'
              aria-selected={selectedTab === TabOption.Monitor}
            >
              Monitor{doesGuardianSenseManipulation ? ' 🚨' : ''}
            </HeaderSegmentedControlOption>
            <HeaderSegmentedControlOption
              isActive={selectedTab === TabOption.Liquidate}
              onClick={() => setSearchParams({ [SELECTED_TAB_KEY]: TabOption.Liquidate })}
              role='tab'
              aria-selected={selectedTab === TabOption.Liquidate}
            >
              Liquidate
            </HeaderSegmentedControlOption>
          </div>
          <HeaderDividingLine />
        </div>
        {tabContent}
      </div>
      <PendingTxnModal
        isOpen={isPendingTxnModalOpen}
        txnHash={pendingTxn}
        setIsOpen={(isOpen: boolean) => {
          setIsPendingTxnModalOpen(isOpen);
          if (!isOpen) {
            setPendingTxn(null);
          }
        }}
        onConfirm={() => {
          setPendingTxn(null);
          setIsPendingTxnModalOpen(false);
          refetchLenderData();
          refetchBalances();
          refetchBorrowerNftRefs().then(() => refetchBorrowers());
        }}
        status={pendingTxnModalStatus}
      />
    </AppPage>
    </>
  );
}
